import type { ReactNode } from 'react'

import { BackLink, type PageBack } from '@/components/encuentro/page/back-link'
import { PageActionButton, type PageAction } from '@/components/encuentro/page/page-action'
import { cn } from '@/lib/utils'

export type PageHeaderProps = {
  /** Names the place: "Personas", "Asado de la promo 2009". The page's only `h1`. */
  title: string
  /** The large title (`display`): the Home greeting, an event or a profile. */
  display?: boolean
  /**
   * Context above the title, in `accent-text`: "Promo 2009 · Presencial", a
   * date. A paragraph, never a heading, so the institution's name can go
   * here and still never be a heading.
   */
  eyebrow?: ReactNode
  /** One line with a fact that orients ("1.248 exalumnos en 31 países"). Never repeats the title. */
  subtitle?: ReactNode
  /**
   * "‹ Eventos" above the title. Shown from 1024 px; below, the frame's top
   * bar owns Back (F7).
   */
  back?: PageBack
  /**
   * The page's one primary action: the task the person came to do. One
   * object, not a list, so a second primary does not compile.
   */
  primaryAction?: PageAction
  /** Up to two secondary actions, before the primary. */
  secondaryActions?: readonly [PageAction] | readonly [PageAction, PageAction]
  /**
   * For a narrow pane (the Mensajes list): the title only asks for its own
   * width instead of 20 rem, so title and actions share the row whenever
   * both fit and the actions wrap under the title otherwise. Never squeezes
   * a button.
   */
  compact?: boolean
  className?: string
}

/**
 * Header of every Encuentro page (docs/redesign/sistema/PageHeader.md): the
 * page's only `h1`, an optional eyebrow and subtitle, and at most one primary
 * action. The actions sit to the right of the title and wrap under it when
 * the row runs out of room (phone, text at 200 %).
 */
export function PageHeader({
  title,
  display = false,
  eyebrow,
  subtitle,
  back,
  primaryAction,
  secondaryActions,
  compact = false,
  className,
}: PageHeaderProps) {
  const hasActions = Boolean(primaryAction || secondaryActions?.length)

  return (
    <header
      data-slot="page-header"
      className={cn(
        'flex min-w-0 flex-wrap items-end justify-between gap-4 font-en-sans text-en-ink',
        className
      )}
    >
      <div className={cn('grid min-w-0 flex-1 gap-2', compact ? 'basis-auto' : 'basis-80')}>
        {back && <BackLink back={back} className="hidden lg:inline-flex" />}
        {eyebrow && <p className="text-en-label text-en-accent-text">{eyebrow}</p>}
        {/* With large text on a phone (`en-large-text`, globals.css) a word
            longer than the line breaks where the page's `lang` hyphenates
            it. Only then: with the default text `hyphens: auto` would also
            cut words that fit on the next line ("North-/field"). `anywhere`
            catches what has no hyphenation point and keeps the title from
            widening the page. */}
        <h1
          className={cn(
            'text-balance [overflow-wrap:anywhere] en-large-text:hyphens-auto',
            display ? 'text-en-display' : 'text-en-title'
          )}
        >
          {title}
        </h1>
        {subtitle && <p className="text-en-subheading font-normal text-en-ink-muted">{subtitle}</p>}
      </div>
      {hasActions && (
        <div data-slot="page-header-actions" className="flex max-w-full flex-wrap gap-2">
          {secondaryActions?.map((action) => (
            <PageActionButton key={action.label} action={action} variant="secondary" />
          ))}
          {primaryAction && <PageActionButton action={primaryAction} variant="primary" />}
        </div>
      )}
    </header>
  )
}
